// Request bodies
import type { Token } from './tokens';

export interface CreateDeploymentRequestBody {
  project_id: string;
  name: string;
  connection_url?: string;
}

export interface GetDeploymentsRequestBody {
  projectId: string;
}

export interface GetDeploymentDetailsRequestBody {
  projectId: string;
  deploymentId: string;
}

export interface GetNeo4jConfigsRequestBody {
  projectId: string;
  deploymentId: string;
}

export interface RenameDeploymentRequestBody {
  projectId: string;
  deploymentId: string;
  name: string;
}

export interface DeleteDeploymentRequestBody {
  projectId: string;
  deploymentId: string;
}

export interface UpdateConnectionUrlRequestBody {
  projectId: string;
  deploymentId: string;
  connectionUrl: string;
}

// Response payloads
export interface DeploymentResponse {
  deployment_id: string;
  name: string;
  created_by: string;
  status: DEPLOYMENT_STATUS;
  connection_url?: string;
}

export interface DeploymentDetailsResponse extends DeploymentResponse {
  dbms?: Dbms;
  token?: Token;
}

export interface Neo4jConfigResponse {
  server_id: string;
  neo4j_config: Record<string, string | number | boolean>;
}

export interface Dbms {
  dbms_id: string;
  status: DEPLOYMENT_STATUS;
  edition: string;
  packaging: string;
  version: string;
  databases: Database[];
  servers: Server[];
  license?: License;
  bloom_license?: License;
  gds_license?: License;
  metric_collection_enabled: boolean;
}

export enum DEPLOYMENT_STATUS {
  RUNNING = 'running',
  DEGRADED = 'degraded',
  OFFLINE = 'offline',
  UNMONITORED = 'unmonitored',
}

export enum LICENSE_TYPE {
  FREE = 'FREE',
  COMMERCIAL = 'COMMERCIAL',
  EVALUATION = 'EVALUATION',
  UNSUPPORTED = 'UNSUPPORTED',
  INCONSISTENT = 'INCONSISTENT',
}

export enum LICENSE_STATE {
  VALID = 'VALID',
  EXPIRED = 'EXPIRED',
  NOT_ACCEPTED = 'NOT_ACCEPTED',
  INVALID = 'INVALID',
  UNKNOWN = 'UNKNOWN',
  INCONSISTENT = 'INCONSISTENT',
}

export interface Database {
  database_id: string;
  name: string;
}

export interface Server {
  server_id: string;
  name: string;
  displayName: string;
  address: string;
  health: string;
  state?: string;
  mode_constraint: string;
  version?: string;
  status: DEPLOYMENT_STATUS;
  last_ping: Date;
  database_instances: DatabaseInstance[];
  license?: License;
  bloom_license?: License;
  gds_license?: License;
  plugin_version: string;
}

export interface DatabaseInstance {
  database_id: string;
  database_name: string;
  role: string;
  aliases: string[];
  access: string;
  requested_status: string;
  current_status: string;
  status_message?: string;
  default: boolean;
  current_primaries_count?: number;
  current_secondaries_count?: number;
  requested_primaries_count?: number;
  requested_secondaries_count?: number;
  creation_time: Date;
  last_start_time: Date;
  store: string;
  writer: boolean;
  last_committed_txn?: number;
  replication_lag: number;
  graph_shards?: string[];
  property_shards?: string[];
  type: string;
}

export interface License {
  type: LICENSE_TYPE;
  state: LICENSE_STATE;
  days_left_on_trial: number;
  total_trial_days: number;
}
